import { Vector3 } from "three";
import type { HitResult } from "./collision";
import { DIRECTION_DOT_THRESHOLD } from "./constants";
import { TENOUCHI_SNAP_THRESHOLD } from "./grading";
import type { CutDirection } from "./beatmap";
import type { Tutorial, TutorialPracticeCheck } from "./tutorials";

const DIAGONAL = Math.SQRT1_2;
const THATS_IT = "so - that's it";
const AGAIN = "again";

const DIR_VECTORS: Record<CutDirection, Vector3 | null> = {
  right: new Vector3(1, 0, 0),
  left: new Vector3(-1, 0, 0),
  up: new Vector3(0, 1, 0),
  down: new Vector3(0, -1, 0),
  "up-left": new Vector3(-DIAGONAL, DIAGONAL, 0),
  "up-right": new Vector3(DIAGONAL, DIAGONAL, 0),
  "down-left": new Vector3(-DIAGONAL, -DIAGONAL, 0),
  "down-right": new Vector3(DIAGONAL, -DIAGONAL, 0),
  any: null,
};

export interface TutorialAttemptFeedback {
  ok: boolean;
  headline: string;
  detail: string;
}

export function evaluateTutorialCut(
  tutorial: Tutorial,
  hit: HitResult,
  practiceCheck?: TutorialPracticeCheck,
): TutorialAttemptFeedback {
  const check = practiceCheck ?? tutorial.training.check;
  if (!check) {
    return {
      ok: true,
      headline: THATS_IT,
      detail: "The shape is enough for this drill.",
    };
  }

  const correction = check.correction ?? tutorial.correction;
  const expected = DIR_VECTORS[check.dir];
  const directionOk =
    expected == null || hit.cutDir.dot(expected) > DIRECTION_DOT_THRESHOLD;
  if (!directionOk) {
    return {
      ok: false,
      headline: AGAIN,
      detail: correction,
    };
  }

  if (check.requireTenouchi && hit.snap < TENOUCHI_SNAP_THRESHOLD) {
    return {
      ok: false,
      headline: AGAIN,
      detail: correction,
    };
  }

  return {
    ok: true,
    headline: THATS_IT,
    detail:
      check.success ??
      (check.requireTenouchi
        ? "Clean line with the squeeze at contact."
        : "Clean line. Finish the motion."),
  };
}
